import type { BuildJobStatus } from '@rentflowdev/database'

import type { PaginationParams } from './common'

export type { BuildJobStatus }

/** Job statuses that mean the build is still in progress (worth polling). */
export const ACTIVE_BUILD_JOB_STATUSES = [
	'QUEUED',
	'BUILDING',
	'SIGNING',
	'PUBLISHING',
] as const satisfies readonly BuildJobStatus[]

export const BUILD_PUBLISH_TYPES = ['MANUAL', 'INSTANTLY', 'DELAYED'] as const
export type BuildPublishType = typeof BUILD_PUBLISH_TYPES[number]

// ── Build profile JSON blobs (canonical shape consumed by the mobile CI) ──

export interface BuildProfileBranding {
	primaryColor: string
	accentColor: string
	backgroundColor: string
	/**
	 * Public https URL of the uploaded logo (see `generateBuilderUploadUrl`),
	 * or an empty string to use the default RentFlow logo.
	 */
	logo: string
}

export interface BuildProfileBackend {
	apiBaseUrl: string
	oauthBaseUrl: string
	oauthClientId: string
	oauthRedirectUri: string
	orgId: string
}

export interface BuildProfileFeatures {
	payments: boolean
	documents: boolean
	archive: boolean
	tickets: boolean
	chat: boolean
	negotiation: boolean
	termination: boolean
	kyc: boolean
	push: boolean
}

export interface BuildProfileLegal {
	termsUrl?: string
	privacyUrl?: string
}

export interface BuildProfileStoreMeta {
	shortDescription?: string
	fullDescription?: string
	whatsNew?: string
	email?: string
	minAndroidVersion?: string
	publishType?: BuildPublishType
}

// ── Build profile ─────────────────────────────────────────

export interface BuildProfile {
	id: string
	clientSlug: string
	displayName: string
	appName: string
	applicationId: string
	versionName: string
	versionCode: number
	branding: BuildProfileBranding
	backend: BuildProfileBackend
	features: BuildProfileFeatures
	legal: BuildProfileLegal | null
	storeMeta: BuildProfileStoreMeta | null
	/**
	 * Presence flag for stored RuStore publish credentials — the credentials
	 * themselves are write-only and never returned by the backend.
	 */
	hasRustoreCredentials: boolean
	isActive: boolean
	createdAt: string
	updatedAt: string
}

export interface ListBuildProfilesParams extends PaginationParams {
	includeInactive?: boolean
}

export interface ListBuildProfilesResponse {
	profiles: BuildProfile[]
	total: number
}

/**
 * Consumer-facing create surface. Technical identity — `clientSlug`,
 * `applicationId`, `displayName`, versions and the backend config — is
 * derived server-side from `appName` and exposed read-only on `BuildProfile`.
 */
export interface CreateBuildProfileBody {
	/** App name shown to end users (2–30 characters). */
	appName: string
	branding: BuildProfileBranding
	features: BuildProfileFeatures
	legal?: BuildProfileLegal
	storeMeta?: BuildProfileStoreMeta
	/** Write-only RuStore key id — stored encrypted, never returned. */
	rustoreKeyId?: string
	/** Write-only RuStore private key — stored encrypted, never returned. */
	rustorePrivateKey?: string
}

export interface UpdateBuildProfileBody {
	appName?: string
	branding?: BuildProfileBranding
	features?: BuildProfileFeatures
	legal?: BuildProfileLegal
	storeMeta?: BuildProfileStoreMeta
	isActive?: boolean
	/**
	 * Write-only RuStore key id. `undefined` keeps the stored value,
	 * an empty string clears it.
	 */
	rustoreKeyId?: string
	/**
	 * Write-only RuStore private key. `undefined` keeps the stored value,
	 * an empty string clears it.
	 */
	rustorePrivateKey?: string
}

// ── Logo upload ───────────────────────────────────────────

export interface GenerateBuilderUploadUrlBody {
	contentType: string
	fileName?: string
}

export interface GenerateBuilderUploadUrlResponse {
	/** Short-lived presigned PUT URL — upload the logo file directly to it. */
	uploadUrl: string
	/** Public URL of the uploaded logo — goes into `branding.logo` after PUT. */
	publicUrl: string
}

// ── Build job ─────────────────────────────────────────────

export interface EnqueueBuildBody {
	/**
	 * Publish the produced build to RuStore after signing.
	 * Requires stored RuStore credentials on the profile.
	 */
	publish?: boolean
}

export interface BuildJob {
	id: string
	profileId: string
	status: BuildJobStatus
	versionName: string
	versionCode: number
	requestedBy: string | null
	artifactUrl: string | null
	error: string | null
	rustoreVersionId: string | null
	startedAt: string | null
	finishedAt: string | null
	createdAt: string
	updatedAt: string
}

export interface ListBuildJobsParams extends PaginationParams {
	profileId: string
}

export interface ListBuildJobsResponse {
	jobs: BuildJob[]
	total: number
}
